<template>
  <!--
    A still, instant mock-up of the player built straight from `model`.
    It never loads video, so every settings change (splash image, watermark,
    play button, overlay, colours) shows up on the next keystroke.
  -->
  <div class="gv-canvas" :class="theme" :style="canvasStyle">
    <img
      v-if="watermarkSource"
      class="gv-canvas__watermark"
      :class="'is-' + watermarkPosition"
      :src="watermarkSource"
      alt=""
    />

    <div v-if="showPlayButton" class="gv-canvas__play">
      <div :key="playAnimation + customPlayButton" :class="playAnimation">
      <img
        v-if="customPlayButton"
        class="gv-canvas__play-image"
        :src="customPlayButton" :style="playImageStyle"
        alt=""
      />
      <span v-else class="gv-canvas__play-circle" :class="theme" :style="playCircleStyle">
        <gs-icon name="play" size="22" />
      </span>
      </div>
    </div>

    <div v-if="showUnmutePill" class="gv-canvas__unmute">
      <img :key="unmuteSource + unmuteAnimation" :src="unmuteSource" :class="unmuteAnimation" :style="unmuteStyle" alt="Click for sound" />
    </div>

    <div v-if="showOverlay" class="gv-canvas__overlay">{{ overlayText }}</div>

    <div v-if="showControls" class="gv-canvas__controls" :style="controlsStyle">
      <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true"><path d="m7 4 14 8-14 8Z" fill="currentColor" /></svg> <span class="gv-canvas__time">0:00</span>
      <span class="gv-canvas__scrubber">
        <span class="gv-canvas__scrubber-fill" :style="tintStyle"></span>
      </span>
      <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2"><path d="m11 5-6 4H2v6h3l6 4V5ZM16 8a5 5 0 0 1 0 8M20 4a11 11 0 0 1 0 16" /></svg><svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2"><path d="M8 3H3v5m13-5h5v5M3 16v5h5m13-5v5h-5" /></svg>
    </div>
  </div>
</template>

<script>
import { videoFormat } from '@/player/videoFormat';
import 'animate.css';
import { enabled, playSource, soundSource, playSize, soundSize, animationClass } from './player/overlayPreview';
const DEFAULT_PRIMARY = 'rgba(253, 100, 129, 1)';

export default {
  name: 'EditorCanvasPreview',

  props: {
    model: {
      type: Object,
      default: () => ({}),
    },
  },

  computed: {
    theme() { return this.settings.theme || 'vjs-theme-default'; },
    playAnimation() { return animationClass(this.model.play_button?.animation); },
    unmuteAnimation() { return animationClass(this.settings.unmute_animation); },
    unmuteSource() { return soundSource(this.model); },
    unmuteStyle() { return { height: soundSize(this.settings.unmute_size) + 'px' }; },
    playImageStyle() { return { width: playSize(this.model.play_button?.size) + 'px', height: playSize(this.model.play_button?.size) + 'px' }; },
    showControls() { return enabled(this.settings.control); },
    controlsStyle() { return { background: this.primaryColor, color: this.settings.color?.secondary || '#fff' }; },
    settings() {
      return this.model.settings || {};
    },

    primaryColor() {
      const color = this.settings.color || {};
      return color.primary || DEFAULT_PRIMARY;
    },

    posterImage() {
      return (
        this.settings.splashImg || this.model.thumbnail || this.model.thumbnail_url || ''
      );
    },

    canvasStyle() {
      const dimensions = this.model.video_dimensions || {};
      const format = videoFormat(this.model);
      const ratio = format ? (format === '9:16' ? 16 / 9 : 9 / 16) : Number(dimensions.type) === 2 && Number(dimensions.width) > 0 && Number(dimensions.height) > 0 ? Number(dimensions.height) / Number(dimensions.width) : (Number(this.model.dimension_percentage) / 100 || 9 / 16);
      return { paddingTop: (ratio * 100) + '%', ...(format === '9:16' ? { paddingTop: '0', aspectRatio: '9 / 16', width: 'min(100%, 320px)', height: 'auto', margin: '0 auto' } : {}), ...(this.posterImage ? { backgroundImage: `url(${JSON.stringify(this.posterImage)})` } : {}) };
    },

    tintStyle() {
      return { background: this.settings.color?.secondary || '#fff' };
    },

    playCircleStyle() {
      return { background: this.primaryColor };
    },

    watermarkSource() {
      const watermark = this.settings.watermark || {};
      return watermark.source || '';
    },

    watermarkPosition() {
      const watermark = this.settings.watermark || {};
      return watermark.position || 'top-right';
    },

    showPlayButton() {
      const playButton = this.model.play_button;
      return Boolean(playButton) && enabled(playButton.enable);
    },

    customPlayButton() {
      return playSource(this.model);
    },

    showUnmutePill() {
      return enabled(this.settings.autoplay) && enabled(this.settings.autoplay_settings?.show_illustration);
    },

    showOverlay() {
      return enabled(this.settings.overlay?.enable);
    },

    overlayText() {
      return (this.settings.overlay && this.settings.overlay.txt) || '';
    },
  },
};
</script>

<style scoped>
.gv-canvas {
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  overflow: hidden;
  background-color: var(--gs-foreground);
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  border-radius: var(--gs-radius-md);
}

.gv-canvas__watermark {
  position: absolute;
  z-index: 2;
  max-width: 22%;
  max-height: 22%;
}

.gv-canvas__watermark.is-top-left {
  top: 12px;
  left: 12px;
}

.gv-canvas__watermark.is-top-right {
  top: 12px;
  right: 12px;
}

.gv-canvas__watermark.is-bottom-left {
  bottom: 32px;
  left: 12px;
}

.gv-canvas__watermark.is-bottom-right {
  right: 12px;
  bottom: 32px;
}

.gv-canvas__play {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 3;
  transform: translate(-50%, -50%);
}

.gv-canvas__play-image {
  display: block;
  max-width: 100%;
  object-fit: contain;
}

.gv-canvas__play-circle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  padding-left: 4px;
  color: var(--gs-primary-foreground);
  border-radius: 999px;
  box-shadow: var(--gs-shadow-md);
}

.gv-canvas__unmute { position: absolute; top: 10px; left: 10px; z-index: 3; max-width: calc(100% - 20px); }
.gv-canvas__unmute img { display: block; max-width: 100%; object-fit: contain; }
.gv-canvas__play-circle.vjs-theme-youtube, .gv-canvas__play-circle.vjs-theme-vimeo { width: 96px; height: 60px; border-radius: 12px; }
.gv-canvas__play-circle.vjs-theme-wistia { border-radius: 0; }
.gv-canvas__controls { gap: 8px; color: white; }
.gv-canvas__controls svg { flex: none; }
.gv-canvas__time { font-size: 10px; }
@media(prefers-reduced-motion:reduce) { .animate__animated { animation: none !important; } }

.gv-canvas__overlay {
  position: absolute;
  right: 0;
  top: 0;
  left: 0;
  z-index: 2;
  padding: 8px 14px;
  overflow: hidden;
  color: var(--gs-primary-foreground);
  font-size: 13px;
  font-weight: 600;
  text-align: center;
  text-overflow: ellipsis;
  white-space: normal;
  background: var(--gs-foreground);
}

.gv-canvas__controls {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 4;
  display: flex;
  align-items: center;
  height: 22px;
  padding: 0 10px;
  background: var(--gs-foreground);
}

.gv-canvas__scrubber {
  display: block;
  width: 100%;
  height: 4px;
  overflow: hidden;
  background: var(--gs-border-strong);
  border-radius: 999px;
}

.gv-canvas__scrubber-fill {
  display: block;
  width: 32%;
  height: 100%;
  border-radius: 999px;
}
</style>
